import { useState, useEffect, useCallback, useRef } from 'react'
import { LocalStorage } from '../helpers/LocalStorage'
import {
    LiveConversation,
    LiveFeedMessage,
} from '../components/AdminPanel/Lori Manager/liveTypes'

const MAX_RECONNECT_DELAY = 32000
const BASE_DELAY = 2000
const AUTH_ERROR_CODE = 4003

interface UseAdminLiveFeedReturn {
    liveCount: number
    conversations: LiveConversation[]
    isConnected: boolean
    hasInitialState: boolean
}

export const useAdminLiveFeed = (): UseAdminLiveFeedReturn => {
    const [liveCount, setLiveCount] = useState(0)
    const [conversations, setConversations] = useState<LiveConversation[]>([])
    const [isConnected, setIsConnected] = useState(false)
    const [hasInitialState, setHasInitialState] = useState(false)

    const socketRef = useRef<WebSocket | null>(null)
    const reconnectAttemptRef = useRef(0)
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    )
    const unmountedRef = useRef(false)

    const connect = useCallback(() => {
        if (unmountedRef.current) return

        const wsBaseUrl = process.env.REACT_APP_WEBSOCKET_BASE_URL
        if (!wsBaseUrl) return

        const token = localStorage.getItem(LocalStorage.EZTURN4_TOKEN)
        if (!token) return

        if (socketRef.current) {
            socketRef.current.close(1000)
            socketRef.current = null
        }

        const url = `${wsBaseUrl}admin/live-feed/?token=${token}`
        const ws = new WebSocket(url)

        ws.onopen = () => {
            if (unmountedRef.current) {
                ws.close(1000)
                return
            }
            reconnectAttemptRef.current = 0
            setIsConnected(true)
        }

        ws.onmessage = (event) => {
            const data: LiveFeedMessage = JSON.parse(event.data)

            switch (data.type) {
                case 'initial_state':
                    setLiveCount(data.count)
                    setConversations(data.conversations)
                    setHasInitialState(true)
                    break
                case 'conversation_started': {
                    setLiveCount(data.count)
                    if (data.conversation) {
                        const incoming = data.conversation
                        setConversations((prev) => {
                            if (prev.some((c) => c.id === incoming.id)) {
                                return prev
                            }
                            return [...prev, incoming]
                        })
                    } else {
                        console.warn(
                            'conversation_started received without conversation payload; row will appear on next reconnect',
                            {
                                conversation_id: data.conversation_id,
                                user_id: data.user_id,
                                company_id: data.company_id,
                            },
                        )
                    }
                    break
                }
                case 'conversation_activity': {
                    const targetId = Number(data.conversation_id)
                    setConversations((prev) =>
                        prev.map((c) =>
                            c.id === targetId
                                ? {
                                      ...c,
                                      last_user_message_at:
                                          data.last_user_message_at,
                                      message_count: c.message_count + 1,
                                  }
                                : c,
                        ),
                    )
                    break
                }
                case 'conversation_takeover': {
                    const targetId = Number(data.conversation_id)
                    setConversations((prev) =>
                        prev.map((c) =>
                            c.id === targetId
                                ? {
                                      ...c,
                                      takeover_admin_id: data.takeover_admin_id,
                                  }
                                : c,
                        ),
                    )
                    break
                }
                case 'conversation_release': {
                    const targetId = Number(data.conversation_id)
                    setConversations((prev) =>
                        prev.map((c) =>
                            c.id === targetId
                                ? { ...c, takeover_admin_id: null }
                                : c,
                        ),
                    )
                    break
                }
                case 'conversation_ended': {
                    const targetId = Number(data.conversation_id)
                    setLiveCount(data.count)
                    setConversations((prev) =>
                        prev.filter((c) => c.id !== targetId),
                    )
                    break
                }
            }
        }

        ws.onclose = (event) => {
            setIsConnected(false)
            setHasInitialState(false)
            socketRef.current = null

            if (
                unmountedRef.current ||
                event.code === 1000 ||
                event.code === AUTH_ERROR_CODE
            ) {
                return
            }

            const delay = Math.min(
                Math.pow(2, reconnectAttemptRef.current) * BASE_DELAY,
                MAX_RECONNECT_DELAY,
            )
            reconnectAttemptRef.current++

            reconnectTimeoutRef.current = setTimeout(() => {
                if (!unmountedRef.current) {
                    connect()
                }
            }, delay)
        }

        ws.onerror = () => {
            // onclose will fire after onerror, reconnection handled there
        }

        socketRef.current = ws
    }, [])

    useEffect(() => {
        unmountedRef.current = false
        connect()

        return () => {
            unmountedRef.current = true
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current)
            }
            if (socketRef.current) {
                socketRef.current.close(1000)
                socketRef.current = null
            }
        }
    }, [connect])

    return { liveCount, conversations, isConnected, hasInitialState }
}
